import React, { useCallback, useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  QrCode, Download, Copy, ExternalLink, RefreshCw, CheckCircle, AlertCircle,
  Video as VideoIcon, Headphones, FileText, Mail, User as UserIcon, Lock,
  Loader2, Share, Package,
} from "lucide-react";
import { toast } from "sonner";
import { qrCodeAPI } from "../../services/api";

// ───────────────────────────────────────────────────────────────────
// Iter-40 — "QR & Public Profile"
//
// Replaces the old QR Code Generator page. The previous design
// generated QR codes that pointed at a non-existent domain/route, had
// a useless infinite history grid, and showed no relationship to what
// the candidate actually had published.
//
// New design:
//   • ONE canonical Public Profile QR anchored to the live recruiter
//     URL ({PUBLIC_BASE_URL}/video/{user_id}).
//   • "What this QR currently points to" status panel — live pulses
//     for Video intro, Audio intro, Profile, Resume, Cover Letter.
//   • Strong empty state when nothing is published.
//   • Separate section explaining Application Package QRs are locked
//     snapshots and listing recent printed packages.
// ───────────────────────────────────────────────────────────────────

const fmtDuration = (s) => {
  if (!s && s !== 0) return "—";
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

// Inline status row — green/amber pill + label + tail.
const StatusRow = ({ icon: Icon, label, ok, tail, testId }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0" data-testid={testId}>
    <div className="flex items-center gap-2.5">
      <Icon className="h-4 w-4 text-slate-500" />
      <span className="text-sm text-slate-700">{label}</span>
    </div>
    <div className="flex items-center gap-2">
      {ok ? (
        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-status="ok">
          <CheckCircle className="h-3 w-3 mr-1" /> Live
        </Badge>
      ) : (
        <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100" data-status="missing">
          <AlertCircle className="h-3 w-3 mr-1" /> Missing
        </Badge>
      )}
      {tail && <span className="text-xs text-slate-500">{tail}</span>}
    </div>
  </div>
);

const QRCodePublicProfilePage = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await qrCodeAPI.getStatus();
      setStatus(data);
    } catch (err) {
      console.error("QR status load failed", err);
      toast.error("Could not load QR status — please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setRegenerating(true);
    try {
      await qrCodeAPI.createQRCode({});
      toast.success("QR code refreshed.");
      await load();
    } catch (err) {
      toast.error(err?.message || "Could not generate QR code.");
    } finally {
      setRegenerating(false);
    }
  };

  const handleCopy = () => {
    if (!status?.public_url) return;
    navigator.clipboard.writeText(status.public_url);
    toast.success("Public profile URL copied.");
  };

  const handleDownload = () => {
    if (!status?.qr?.qr_code_url) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const link = document.createElement("a");
    link.href = `${backendUrl}${status.qr.qr_code_url}`;
    link.download = `inter-view-qr-${status.qr.id}.png`;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("QR code download started.");
  };

  const handleShare = () => {
    if (!status?.public_url) return;
    if (navigator.share) {
      navigator.share({
        title: "My Inter-View public profile",
        text: "Scan or open my public recruiter profile.",
        url: status.public_url,
      }).catch(() => { /* user dismissed */ });
    } else {
      handleCopy();
    }
  };

  const handleOpenPreview = () => {
    if (status?.public_url) window.open(status.public_url, "_blank");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="qr-loading">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!status) return null;

  const videoOk = status.video_intro?.published;
  const audioOk = status.audio_intro?.published;
  const anyIntroPublished = videoOk || audioOk;
  const pMode = status.presentation_mode;
  const showHiddenWarning = pMode === "hidden";

  const qrImageUrl = status.qr?.qr_code_url
    ? `${process.env.REACT_APP_BACKEND_URL}${status.qr.qr_code_url}`
    : null;

  // Live "what this QR resolves to" copy for the destination card.
  const destinationLine = (() => {
    if (showHiddenWarning) return "Hidden — recruiters scanning this QR will see your profile/resume but no intro media.";
    if (pMode === "video" && videoOk) return "Recruiters land on your published video introduction first.";
    if (pMode === "audio" && audioOk) return "Recruiters land on your published audio introduction first.";
    if (pMode === "video" && !videoOk) return "Set to video mode but no video intro is published yet — recruiters will see your profile only.";
    if (pMode === "audio" && !audioOk) return "Set to audio mode but no audio intro is published yet — recruiters will see your profile only.";
    return "Recruiters land on your profile page.";
  })();

  return (
    <div className="space-y-8" data-testid="qr-public-profile">
      {/* ─── Header ─────────────────────────────────────── */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">QR &amp; Public Profile</h1>
        <p className="text-slate-600 mt-1 max-w-2xl">
          One QR code that recruiters scan to access your live public profile. It always reflects what's currently published on your account.
        </p>
      </div>

      {/* ─── Primary card: QR + Destination ─────────────── */}
      <Card data-testid="qr-primary-card" className="border-slate-200">
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-xl">
                <QrCode className="h-5 w-5 text-blue-600" />
                Your Public Profile QR
              </CardTitle>
              <CardDescription className="mt-1">
                {anyIntroPublished
                  ? destinationLine
                  : "No intro is published yet. Recruiters scanning this QR will land on your profile."}
              </CardDescription>
            </div>
            {!anyIntroPublished && (
              <Badge className="bg-amber-100 text-amber-700">
                <AlertCircle className="h-3 w-3 mr-1" />
                Limited content
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8">
            {/* QR image or empty state */}
            <div className="flex flex-col items-center" data-testid="qr-image-container">
              {qrImageUrl ? (
                <>
                  <div className="bg-white p-5 rounded-2xl border-2 border-blue-100 shadow-md">
                    <img
                      src={qrImageUrl}
                      alt="Your public profile QR code"
                      className="w-52 h-52"
                      data-testid="qr-image"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    Generated {fmtDate(status.qr?.created_at)}
                  </p>
                </>
              ) : (
                <div
                  className="w-64 h-64 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center p-6 text-center"
                  data-testid="qr-empty-state"
                >
                  <QrCode className="h-12 w-12 text-slate-300 mb-3" />
                  <p className="text-sm font-medium text-slate-700">No QR code yet</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Click <span className="font-semibold">Generate QR</span> below to create one anchored to your profile.
                  </p>
                </div>
              )}
            </div>

            {/* Right column: live destination + actions */}
            <div className="space-y-5 min-w-0">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">
                  This QR currently points to
                </p>
                <div className="space-y-0">
                  <StatusRow
                    icon={VideoIcon}
                    label="Video introduction"
                    ok={videoOk}
                    tail={videoOk ? `${fmtDuration(status.video_intro.duration_seconds)} · ${fmtDate(status.video_intro.published_at)}` : "Not published"}
                    testId="qr-status-video"
                  />
                  <StatusRow
                    icon={Headphones}
                    label="Audio introduction"
                    ok={audioOk}
                    tail={audioOk ? `${fmtDuration(status.audio_intro.duration_seconds)} · ${fmtDate(status.audio_intro.published_at)}` : "Not published"}
                    testId="qr-status-audio"
                  />
                  <StatusRow
                    icon={UserIcon}
                    label="Profile"
                    ok={status.profile_complete}
                    tail={status.profile_complete ? "Complete" : "Add name + professional title"}
                    testId="qr-status-profile"
                  />
                  <StatusRow
                    icon={FileText}
                    label="Resume"
                    ok={status.has_resume}
                    tail={status.has_resume ? "Linked" : "Not built yet"}
                    testId="qr-status-resume"
                  />
                  <StatusRow
                    icon={Mail}
                    label="Cover letter"
                    ok={status.has_cover_letter}
                    tail={status.has_cover_letter ? "Linked" : "Not built yet"}
                    testId="qr-status-cl"
                  />
                </div>
              </div>

              {showHiddenWarning && (
                <div className="rounded-lg border border-slate-300 bg-slate-100 p-3 text-sm text-slate-700" data-testid="qr-hidden-warning">
                  <span className="font-semibold">Hidden mode:</span> recruiters scanning this QR will see your profile + resume / cover letter, but no intro media. Switch to Video or Audio mode in the Introductions tab to lead with an intro.
                </div>
              )}

              {/* Live URL */}
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1.5">
                  Public URL
                </p>
                <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2.5">
                  <code className="flex-1 text-xs font-mono text-slate-700 break-all" data-testid="qr-public-url">
                    {status.public_url || "Not configured"}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopy}
                    disabled={!status.public_url}
                    data-testid="qr-copy-url-btn"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-2">
                {qrImageUrl ? (
                  <>
                    <Button
                      onClick={handleDownload}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="qr-download-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download PNG
                    </Button>
                    <Button variant="outline" onClick={handleShare} data-testid="qr-share-btn">
                      <Share className="h-4 w-4 mr-2" />
                      Share
                    </Button>
                    <Button variant="outline" onClick={handleOpenPreview} data-testid="qr-preview-btn">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open profile
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={handleGenerate}
                      disabled={regenerating}
                      data-testid="qr-regenerate-btn"
                    >
                      {regenerating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                      Regenerate
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={handleGenerate}
                    disabled={regenerating}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="qr-generate-btn"
                  >
                    {regenerating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <QrCode className="h-4 w-4 mr-2" />}
                    Generate QR
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ─── Application Package QRs explainer ─────────── */}
      <Card data-testid="qr-app-packages-card" className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Lock className="h-4 w-4 text-slate-600" />
            Application Package QRs are different
          </CardTitle>
          <CardDescription className="max-w-3xl">
            The QR above always tracks your <em>currently published</em> intro. The QR codes printed on your downloaded <span className="font-semibold">Application Packages</span> are different: they <span className="font-semibold">lock to the intro that was active at the moment you printed the package</span>. So if you re-record later, an old printed package keeps pointing at the exact intro the recruiter expects.
          </CardDescription>
        </CardHeader>
        {status.application_packages.length > 0 && (
          <CardContent>
            <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">
              Recent printed packages ({status.application_packages.length})
            </p>
            <div className="space-y-2" data-testid="qr-app-packages-list">
              {status.application_packages.slice(0, 5).map((p) => {
                const isAudio = p.intro_mode_at_print === "audio";
                const isHidden = p.intro_mode_at_print === "hidden";
                const Icon = isAudio ? Headphones : isHidden ? Lock : VideoIcon;
                return (
                  <div
                    key={p.id}
                    className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg border border-slate-100 hover:bg-slate-50"
                    data-testid={`qr-app-package-row-${p.id}`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Package className="h-4 w-4 text-slate-400 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm text-slate-900 truncate">
                          Package · locked to <span className="font-semibold capitalize">{p.intro_mode_at_print}</span>
                        </p>
                        <p className="text-xs text-slate-500">Printed {fmtDate(p.created_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Badge variant="outline" className="text-[10px]">
                        <Icon className="h-3 w-3 mr-1" /> {p.intro_mode_at_print}
                      </Badge>
                      {p.locked_qr_url && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(p.locked_qr_url, "_blank")}
                          data-testid={`qr-app-package-open-${p.id}`}
                          title="Open this locked package URL"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {status.application_packages.length > 5 && (
              <p className="text-xs text-slate-500 mt-3">
                Showing the 5 most recent of {status.application_packages.length}.
              </p>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default QRCodePublicProfilePage;
